import { Scalar } from "../math";
import { HasToString, IsString } from "../types";
export class FetchError extends Error {
    constructor(message, ...userArgs) {
        super(message);
        this.userArgs = userArgs.length > 0 ? userArgs : [];
    }
}
export class FetchResult {
    static Null(request, userArgs = null) {
        return new FetchResult(request, null, userArgs);
    }
    constructor(address, content, userArgs = null) {
        this.ok = true;
        this.address = address;
        this.content = content;
        this.userArgs = userArgs ?? [];
    }
}
export class DefaultUrlBuilder {
    buildUrl(request, ...params) {
        if (IsString(request)) {
            return request;
        }
        if (HasToString(request)) {
            return request.toString();
        }
        throw new Error("Request must be a string or an object with a toString method.");
    }
}
export class WebClientOptions {
    static getDefault() {
        return new WebClientOptions({
            maxRetry: 3,
            initialDelay: 1000,
        });
    }
    constructor(p) {
        Object.assign(this, p);
    }
}
export class WebClientOptionsBuilder {
    constructor() {
        this._options = {};
    }
    withMaxRetry(v) {
        this._options.maxRetry = v;
        return this;
    }
    withInitialDelay(v) {
        this._options.initialDelay = v;
        return this;
    }
    build() {
        return new WebClientOptions(this._options);
    }
}
export class WebClient {
    constructor(name, codec, urlFactory, options) {
        this._name = name;
        if (!codec) {
            throw new Error(`invalid codec parameter ${codec}`);
        }
        this._urlFactory = urlFactory ?? new DefaultUrlBuilder();
        this._codec = codec;
        this._options = { ...WebClientOptions.getDefault(), ...options };
    }
    get name() {
        return this._name;
    }
    async fetchAsync(request, ...userArgs) {
        if (!request) {
            throw new FetchError("Invalid request parameter.");
        }
        const url = this._urlFactory.buildUrl(request, ...userArgs) ?? request.toString();
        if (!url) {
            throw new FetchError(`Builded url of ${request.toString()} can not be null`);
        }
        const maxRetry = this._options.maxRetry ?? 1;
        let delay = this._options.initialDelay ?? 1000;
        let retryCount = 0;
        do {
            try {
                const response = await fetch(url);
                let content = null;
                if (response.ok) {
                    content = (await this._codec.decodeAsync(response));
                }
                else {
                    console.warn(`Request failed: ${url} (Status: ${response.status})`);
                }
                const result = new FetchResult(request, content, userArgs);
                result.status = response.status;
                result.statusText = response.statusText;
                return result;
            }
            catch (error) {
                console.error(`Error fetching ${url}: ${error.message || error}`);
                if (retryCount >= maxRetry - 1) {
                    throw new FetchError(`Exceeded maximum retries for URL: ${url}`, ...userArgs);
                }
            }
            const jitter = Scalar.GetRandomInt(0, this._options.initialDelay ?? 1000);
            await new Promise((resolve) => setTimeout(resolve, Math.min(delay + jitter, 30000)));
            delay *= 2;
            retryCount++;
        } while (retryCount < maxRetry);
        throw new FetchError(`Exceeded maximum retries for URL: ${url}`, ...userArgs);
    }
}
//# sourceMappingURL=webClient.js.map